import qz from 'qz-tray';

const STORAGE_KEY = 'printer_config';

// Default config
const defaultConfig = {
  packingSlipPrinter: null,
  labelPrinter: null,
  useQzTray: true
};

// Connection state
let qzConnected = false;
let qzAvailable = null; // null = unknown, true/false = checked

// ===============================
// QZ SECURITY INITIALISATION
// ===============================
let qzSecurityInitialised = false;

const initQzSecurity = () => {
  if (qzSecurityInitialised) return;

  qz.security.setSignatureAlgorithm("SHA512");

  qz.security.setCertificatePromise(function(resolve, reject) {
    fetch('/api/qz/certificate/')
      .then(function(res) { return res.text(); })
      .then(resolve)
      .catch(reject);
  });

  qz.security.setSignaturePromise(function(toSign) {
    return function(resolve, reject) {
        fetch('/api/qz/sign/', {
        method: 'POST',
        body: toSign,
        credentials: 'include'
        })
        .then(function(res) { return res.text(); })
        .then(resolve)
        .catch(reject);
    };
    });

    qzSecurityInitialised = true;
  };

// ===============================
// CONFIG STORAGE
// ===============================
export const getPrinterConfig = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultConfig, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to load printer config:', e);
  }
  return defaultConfig;
};

export const setPrinterConfig = (config) => {
  try {
    const current = getPrinterConfig();
    const updated = { ...current, ...config };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error('Failed to save printer config:', e);
    return getPrinterConfig();
  }
};

// ===============================
// QZ CONNECTION MANAGEMENT
// ===============================
export const checkQzAvailable = async () => {
  initQzSecurity();

  if (qzAvailable !== null) {
    return qzAvailable;
  }

  try {
    if (!qz.websocket.isActive()) {
      await qz.websocket.connect();
      qzConnected = true;
    }
    qzAvailable = true;
    return true;
  } catch (e) {
    console.log('QZ Tray not available:', e.message);
    qzAvailable = false;
    return false;
  }
};

export const connectQz = async () => {
  initQzSecurity();

  if (qzConnected && qz.websocket.isActive()) {
    return true;
  }

  try {
    if (!qz.websocket.isActive()) {
      await qz.websocket.connect();
    }
    qzConnected = true;
    qzAvailable = true;
    return true;
  } catch (e) {
    console.error('Failed to connect to QZ Tray:', e);
    qzConnected = false;
    throw e;
  }
};

export const disconnectQz = async () => {
  if (qz.websocket.isActive()) {
    try {
      await qz.websocket.disconnect();
    } catch (e) {
      console.error('Failed to disconnect from QZ Tray:', e);
    }
  }
  qzConnected = false;
};

// ===============================
// PRINTER DISCOVERY
// ===============================
export const getAvailablePrinters = async () => {
  try {
    await connectQz();
    return await qz.printers.find();
  } catch (e) {
    console.error('Failed to get printers:', e);
    return [];
  }
};

// ===============================
// QZ PRINTING
// ===============================
export const printPdfWithQz = async (base64Data, printerName) => {
  await connectQz();

  const config = qz.configs.create(printerName, {
    scaleContent: true,
    rasterize: false
  });

  const data = [{
    type: 'pixel',
    format: 'pdf',
    flavor: 'base64',
    data: base64Data
  }];

  await qz.print(config, data);
};

// ===============================
// SMART PRINT ROUTER
// ===============================
export const printPdf = async (base64Data, printerType = 'label') => {
  const config = getPrinterConfig();
  const printerName = printerType === 'label'
    ? config.labelPrinter
    : config.packingSlipPrinter;

  if (config.useQzTray && printerName) {
    try {
      const available = await checkQzAvailable();
      if (available) {
        await printPdfWithQz(base64Data, printerName);
        return { method: 'qz', printer: printerName };
      }
    } catch (e) {
      console.warn('QZ Tray print failed, falling back to browser:', e);
    }
  }

  return printPdfWithBrowser(base64Data);
};

// ===============================
// BROWSER PDF FALLBACK
// ===============================
export const printPdfWithBrowser = (base64Data) => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  } else {
    window.open(url, '_blank');
  }

  return { method: 'browser', printer: null };
};

// ===============================
// HTML PRINTING
// ===============================
export const printHtml = async (htmlContent, printerType = 'packingSlip') => {
  const config = getPrinterConfig();
  const printerName = printerType === 'label'
    ? config.labelPrinter
    : config.packingSlipPrinter;

  if (config.useQzTray && printerName) {
    try {
      const available = await checkQzAvailable();
      if (available) {
        const html2pdf = (await import('html2pdf.js')).default;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '10000px';
        iframe.style.width = '210mm';
        iframe.style.height = '297mm';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        try {
          // Wait for iframe to fully load with styles
          await new Promise((resolve) => {
            iframe.onload = () => {
              setTimeout(resolve, 200);
            };
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
          });

          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

          const pdfBlob = await html2pdf()
            .from(iframeDoc.documentElement)
            .set({
              margin: 0,
              filename: 'packing-slip.pdf',
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: { 
                scale: 2, 
                useCORS: true,
                logging: false
              },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            })
            .outputPdf('blob');

          const base64 = await blobToBase64(pdfBlob);
          await printPdfWithQz(base64, printerName);
          return { method: 'qz', printer: printerName };
        } finally {
          document.body.removeChild(iframe);
        }
      }
    } catch (e) {
      console.warn('QZ Tray HTML print failed, falling back to browser:', e);
    }
  }

  return printHtmlWithBrowser(htmlContent);
};

// ===============================
// BROWSER HTML FALLBACK
// ===============================
export const printHtmlWithBrowser = (htmlContent) => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  } else {
    alert('Please allow popups to print');
  }
  return { method: 'browser', printer: null };
};

// ===============================
// HELPERS
// ===============================
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const getPrintServiceStatus = async () => {
  const config = getPrinterConfig();
  const available = await checkQzAvailable();

  return {
    qzAvailable: available,
    qzEnabled: config.useQzTray,
    labelPrinter: config.labelPrinter,
    packingSlipPrinter: config.packingSlipPrinter,
    willUseQz: available && config.useQzTray
  };
};

export default {
  getPrinterConfig,
  setPrinterConfig,
  checkQzAvailable,
  connectQz,
  disconnectQz,
  getAvailablePrinters,
  printPdf,
  printPdfWithQz,
  printPdfWithBrowser,
  printHtml,
  printHtmlWithBrowser,
  getPrintServiceStatus
};
